import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { Navigate, useLocation, useParams } from 'react-router'

import { EldStatusPill } from '@/components/EldStatusPill'
import { StatusPill } from '@/components/StatusPill'
import { ActionsSheet } from '@/components/ui/actions-sheet'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Tabs } from '@/components/ui/tabs'
import { useIsBelowLg } from '@/hooks/use-mobile'
import { customerListHref } from '@/lib/customerListFilters'
import { CUSTOMER_FILL_APPLICATION_TAB, customerTabHref } from '@/lib/customerTabs'
import { orgPillStatus, useCurrentUser, useOrganisation } from '../../../../models/user'
import { UsersTab } from '../AdminSettings/OrganisationTab'
import { Certificates } from '../Certificates/Certificates'
import { ScopedAgentChat } from '../Internal/AgentChat/ScopedAgentChat'
import { BusinessCards } from './BusinessCards'
import { ChangedSinceQuoteBanner } from './ChangedSinceQuoteBanner'
import { ClaimsTab } from './ClaimsTab'
import { CustomerActionSidebar } from './CustomerActionSidebar'
import { CustomerDetailSkeleton } from './CustomerDetailSkeleton'
import {
  CUSTOMER_DEFAULT_TAB,
  CUSTOMER_POLICY_CHANGES_TAB,
  CUSTOMER_QUOTES_TAB,
  CUSTOMER_TAB_VALUES,
  CustomerNav,
} from './CustomerNav'
import { CustomerTimeline } from './CustomerTimeline'
import { DocumentsTab } from './DocumentsTab'
import { DriversTab } from './DriversTab'
import { FillApplicationTab } from './FillApplication/FillApplicationTab'
import { InsuranceTab } from './InsuranceTab'
import { LargeFleetPacketCard } from './LargeFleetPacketCard'
import { LossRunsTab } from './LossRunsTab'
import { PolicyChangeTab } from './PolicyChangeTab'
import { PosthogOrgPill } from './PosthogOrgPill'
import { QuoteTab } from './QuoteTab'
import { TimeToQuotingPill } from './TimeToQuotingPill'
import { TrailersTab } from './TrailersTab'
import { TrucksTab } from './TrucksTab'
import { VaultTab } from './VaultTab'

export function CustomerDetail() {
  const params = useParams()
  const id = params.id
  const { organisation, isLoading, refetch } = useOrganisation(id)
  const { user } = useCurrentUser({ enabled: true })
  const location = useLocation()
  const isBelowLg = useIsBelowLg()

  // Old links may point at tabs that no longer exist — fall back to the default.
  // A tab may keep its own place after its segment, e.g. /vault/drivers/<id>.
  const [requestedTab = '', ...tabPath] = (params['*'] ?? '').split('/').filter(Boolean)
  const currentTab = CUSTOMER_TAB_VALUES.includes(requestedTab) ? requestedTab : CUSTOMER_DEFAULT_TAB

  // Tabs used to live in the hash, and links already sent out still say `#coi`.
  const [legacyTab = '', ...legacyPath] = location.hash.slice(1).split('/').filter(Boolean)
  const legacyHref =
    id && CUSTOMER_TAB_VALUES.includes(legacyTab) ? customerTabHref(id, legacyTab, legacyPath.join('/')) : ''

  const [coiTab, setCoiTab] = useState('holders')

  if (legacyHref) {
    return <Navigate to={{ pathname: legacyHref, search: location.search }} replace />
  }

  if (isLoading) {
    return <CustomerDetailSkeleton />
  }

  if (!organisation) {
    return <div className="mt-10 text-center">Customer not found</div>
  }

  return (
    <>
      <Helmet>
        <title>Customer Details</title>
      </Helmet>
      <div className="flex min-w-0 flex-col gap-6">
        {/* On a phone the name takes the first line on its own; the pills follow underneath. */}
        <div className="flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-3">
          <div className="flex items-start gap-3">
            <div className="text-2xl font-semibold wrap-anywhere">{organisation.name}</div>
            <ActionsSheet
              label="Actions"
              title="Customer actions"
              className="ml-auto h-7 shrink-0 px-2 text-xs"
            >
              <CustomerActionSidebar org={organisation} />
              <Button
                variant="link"
                size="sm"
                href={customerListHref(user?.id)}
                className="justify-start px-0"
              >
                <ArrowLeft /> All customers
              </Button>
            </ActionsSheet>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <StatusPill status={orgPillStatus(organisation)} className="inline-flex h-7 items-center" />
            {organisation.isLargeFleet && <Badge variant="secondary">Large Fleet</Badge>}
            <EldStatusPill eld={organisation.eld} />
            <PosthogOrgPill orgId={organisation.id} />
            <TimeToQuotingPill orgId={organisation.id} />
          </div>
        </div>

        {/* The Intake tab's Sent banner says this beside its Requote button. */}
        {currentTab !== CUSTOMER_FILL_APPLICATION_TAB && (
          <ChangedSinceQuoteBanner application={organisation.currentInsuranceApplication} />
        )}

        <Tabs
          value={currentTab}
          activationMode="manual"
          orientation={isBelowLg ? 'horizontal' : 'vertical'}
          className="min-w-0 lg:items-start lg:gap-6"
        >
          {/* The rail's actions move into the header's sheet below `lg`; only the tabs stay. */}
          <div className="flex min-w-0 flex-col gap-4 lg:sticky lg:top-[calc(var(--app-top-bar-height)+1rem)] lg:w-56 lg:shrink-0 lg:self-start">
            <CustomerNav orgId={organisation.id} />
            <Separator className="max-lg:hidden" />
            <div className="flex flex-col gap-4 max-lg:hidden">
              <CustomerActionSidebar org={organisation} />
              <Button
                variant="link"
                size="sm"
                href={customerListHref(user?.id)}
                className="justify-start px-0"
              >
                <ArrowLeft /> All customers
              </Button>
            </div>
          </div>

          <div className="flex max-w-7xl min-w-0 flex-1 flex-col gap-6">
            {/* Carrier and address only on Overview — the other tabs need the height for their own content. */}
            {currentTab === CUSTOMER_DEFAULT_TAB && (
              <>
                <div className="grid gap-6 min-[1280px]:grid-cols-2">
                  <BusinessCards
                    business={organisation.business}
                    orgId={organisation.id}
                    onUpdated={refetch}
                  />
                </div>
                <LargeFleetPacketCard orgId={organisation.id} />
                <UsersTab org={organisation} showImpersonate />
                <CustomerTimeline org={organisation} />
              </>
            )}
            {currentTab === 'documents' && <DocumentsTab orgId={organisation.id} />}
            {currentTab === 'vault' && <VaultTab orgId={organisation.id} path={tabPath} />}
            {currentTab === 'coi' && (
              <Certificates org={organisation} activeTab={coiTab} onTabChange={setCoiTab} />
            )}
            {currentTab === CUSTOMER_FILL_APPLICATION_TAB && (
              // Keyed: staff move between customers without this page unmounting.
              <FillApplicationTab
                key={organisation.currentInsuranceApplication?.id ?? `org-${organisation.id}`}
                org={organisation}
              />
            )}
            {currentTab === 'application' && <QuoteTab orgId={organisation.id} />}
            {currentTab === CUSTOMER_QUOTES_TAB && <QuoteTab orgId={organisation.id} quotesPage />}
            {currentTab === 'insurance' && <InsuranceTab org={organisation} />}
            {currentTab === 'loss-runs' && (
              <LossRunsTab orgId={organisation.id} isStaff={user?.isStaff ?? false} />
            )}
            {currentTab === 'trucks' && <TrucksTab orgId={organisation.id} />}
            {currentTab === 'trailers' && <TrailersTab orgId={organisation.id} />}
            {currentTab === 'drivers' && <DriversTab orgId={organisation.id} />}
            {currentTab === 'claims' && <ClaimsTab orgId={organisation.id} />}
            {currentTab === 'agent-chat' && <ScopedAgentChat scope={{ organisation: organisation.id }} />}
            {currentTab === CUSTOMER_POLICY_CHANGES_TAB && (
              <PolicyChangeTab org={organisation} path={tabPath} />
            )}
          </div>
        </Tabs>
      </div>
    </>
  )
}
